import { ElementRef, Injectable } from '@angular/core';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { combineLatest, EMPTY, filter, first, map, Observable, Subscription, switchMap, take, tap, timer } from 'rxjs';
import { CobrowsingFacade } from '@agent/core/store/cobrowsing/cobrowsing.facade';
import { CobrowsingInfo, CobrowsingMessageKey, CobrowsingSessionState, ToolName } from '@agent/core/store/cobrowsing';

import CobrowseAPI from 'cobrowse-agent-sdk';

import {
  AppConf,
  Booking,
  BookingFacade,
  ChanEnum,
  ChannelUtil,
  CngFacade,
  FormFacade,
  Formly,
  HttpService,
  Logger,
  ZoneService,
  ProfileFacade,
  ProfileService,
  isNotNullOrUndefined,
} from '@contact/upgrade';

interface CobrowsingRunInfo extends CobrowsingInfo {
  cobrowse?: CobrowseAPI;
  context?: any;
  lastUpdate?: number;
  timeoutTimerSubscription?: Subscription;
  extAppSubscription?: Subscription;
  sessionState?: CobrowsingSessionState;
  errorMessageKey?: CobrowsingMessageKey;
  toBeCleaned?: boolean;
}
interface CobrowsingReportingInfo {
  readonly userUuid?: string;
  readonly startDate?: number;
  readonly endDate?: number;
  readonly bookingId?: string;
  readonly connectionId?: string;
  readonly flowId?: string;
  readonly mediaType?: string;
  readonly direction?: string;
  readonly customerUuid?: string;
  readonly endReason?: CobrowsingMessageKey;
  readonly skillsId?: string[];
  readonly domainId?: string;
}
const COBROWSING_FRAME_NAME_PREFIX = 'cobrowsing-frame-';
const COBROWSING_DEFAULT_TIMEOUT = 30000;
const COBROWSING_DEFAULT_MAX_ACTIVE_SESSIONS = 3;
export const COBROWSING_FORM_ID_PREFIX = 'cobrowsing-form';
const EXT_APPLICATION_HIDDEN_CLASS_NAME = 'fake-hidden-for-cobrowsing';

enum CobrowseIoServerMessages {
  ACCOUNT_NOT_FOUND = 'Account not found',
  NETWORK_REQUEST_FAILED = 'Network request failed',
  SESSION_NOT_FOUND = 'Session not found',
}

const IFRAME_OPTIONS = new URLSearchParams({
  messages: 'none',
  popout: 'none',
  navigation: 'none',
  end_action: 'none',
  device_controls: 'none',
  session_details: 'none',
  agent_tools: 'none',
}).toString();

/**
 * Service handling cobrowsing-related function
 * Mainly exist to end the cobrowsing session/close the external app when the call is over
 * Separated from the component so it can trigger even if the component is not created when the call closes.
 */
@Injectable({
  providedIn: 'root',
})
export class CobrowsingService {
  private cobrowsings: Record<string, CobrowsingRunInfo> = {};
  private COBROWSING_SESSION_TIMEOUT: number;
  private COBROWSE_SERVER_URL: string;
  private maxActiveSessions: number;
  private domainList: string[] = [];
  public isSent: boolean = false;

  constructor(
    private readonly http: HttpService,
    private readonly logger: Logger,
    private readonly appConf: AppConf,
    private readonly formFacade: FormFacade,
    private readonly externalAppService: AppExternalService,
    private readonly zoneService: ZoneService,
    private readonly cobrowsingFacade: CobrowsingFacade,
    private readonly profileFacade: ProfileFacade,
    private readonly profileService: ProfileService,
    private readonly bookingFacade: BookingFacade
  ) {
    this.logger = logger.forSource(CobrowsingService.name);
    appConf.pipe(first()).subscribe(({ cobrowsing, phone }) => {
      this.COBROWSING_SESSION_TIMEOUT = cobrowsing?.timeout || COBROWSING_DEFAULT_TIMEOUT;
      this.COBROWSE_SERVER_URL = cobrowsing?.serverUrl || '';
      this.maxActiveSessions = cobrowsing?.maxActiveSessions || COBROWSING_DEFAULT_MAX_ACTIVE_SESSIONS;
      if (phone?.cobrowsingActiveDomains) {
        this.domainList = phone?.cobrowsingActiveDomains?.split(',');
      }
      if (!this.COBROWSE_SERVER_URL) {
        this.logger.warn(`The configuration for the cobrowse.io server url is not present. Cobrowsing will not work properly`);
      }
    });
  }

  /**
   * Calls the API creating a JWT that will be shared with the Cobrowse.io backend
   */
  getCobrowsingJwt(): Observable<string> {
    return this.http.get(`V2/${CngFacade.PROTECTED__COBROWSING}/createCobrowseIoJwt`, { responseType: 'text' });
  }

  async startCobrowsingFromCode(bookingId: string, code: string): Promise<CobrowsingInfo> {
    const currentCobrowsing = this.cobrowsings[bookingId] || {};
    let session;
    try {
      session = await this.getSession(bookingId, code);
    } catch (e) {
      const errorMessageKey = this.handleServerError(currentCobrowsing, e);
      return { bookingId, errorMessageKey };
    }
    return {
      bookingId: currentCobrowsing.bookingId,
      jwt: currentCobrowsing.jwt,
      sessionCode: code,
      sessionId: session?.id,
    };
  }

  /**
   * Sets the external app in the ext app service, attached to the given container.
   * @param info information on the coborwsing to start
   * @param container the Element the external app should be displayed relative to
   */
  createCobrowsingFrame(info: CobrowsingInfo, container: ElementRef) {
    const { bookingId, sessionId } = info;
    this.cobrowsings[info.bookingId] = { ...this.cobrowsings[bookingId], ...info };
    const currentCobrowsing = this.cobrowsings[bookingId];
    if (currentCobrowsing.cobrowse) {
      const frameName = `${COBROWSING_FRAME_NAME_PREFIX}${sessionId}`;
      const url = `${currentCobrowsing.cobrowse.api}/session/${sessionId}?token=${currentCobrowsing.jwt}&${IFRAME_OPTIONS}`;
      if (!currentCobrowsing.extAppSubscription && this.externalAppService.getApplicationContainer(frameName).isEmpty()) {
        currentCobrowsing.extAppSubscription = this.externalAppService
          .createApp(frameName, url, false, this.waitForContentWindow.bind(this), { hiddenClassName: EXT_APPLICATION_HIDDEN_CLASS_NAME })
          .subscribe((iframe: HTMLIFrameElement) => {
            this.setCobrowseContext(bookingId, frameName, iframe!); // this callback is only triggered when iframe.contentWindow is not null so we can use "!" without concern here
            this.displayExternalApp(bookingId, container);
          });
      }
    }
  }

  /**
   * Returns a boolean that completes whenever the contentWindow of a given iframe is actually loaded
   * used for the waiting method given to ExternalAppService.createApp
   * @param iframe the iframe which content window we are waiting for
   * @private
   */
  private waitForContentWindow(iframe: HTMLIFrameElement): Observable<boolean> {
    return timer(0, 500, this.zoneService.leaveScheduler()).pipe(
      map(() => !!iframe.contentWindow),
      filter(b => b),
      first()
    );
  }

  /**
   * Extracts the cobrowse.io RemoteContext from the iframe and sets it in a class variable to handle events
   * @param bookingId the booking id of the interaction with cobrowsing
   * @param frameName the frame name to pass to the external app service
   * @param iframe the iframe element in which the combrowse.io page is loaded
   * @async
   */
  async setCobrowseContext(bookingId: string, frameName: string, frame: HTMLIFrameElement) {
    const formId = Formly.toId([COBROWSING_FORM_ID_PREFIX, bookingId]);

    this.cobrowsings[bookingId].context?.destroy();
    if (this.cobrowsings[bookingId].cobrowse) {
      this.cobrowsings[bookingId].context = await this.cobrowsings[bookingId].cobrowse?.attachContext(frame);

      this.cobrowsings[bookingId].context.on('session.updated', session => {
        const currentCobrowsing = this.cobrowsings[bookingId];
        if (currentCobrowsing.sessionState !== session.state) {
          this.cobrowsingFacade.updateCobrowsingState({ bookingId, sessionState: session.state });
          currentCobrowsing.sessionState = session.state;
        }
        if (session.ended && currentCobrowsing.toBeCleaned) {
          this.logger.debug(
            `End of cobrowsing session ${currentCobrowsing.sessionId} (code ${currentCobrowsing.sessionCode}) for booking ${currentCobrowsing.bookingId}`
          );
          this.cobrowsingFacade.cobrowsingEnded(bookingId);
          currentCobrowsing.context.destroy();
          currentCobrowsing.sessionState = undefined;
          currentCobrowsing.timeoutTimerSubscription?.unsubscribe();
          currentCobrowsing.timeoutTimerSubscription = undefined;
          currentCobrowsing.extAppSubscription?.unsubscribe();
          currentCobrowsing.extAppSubscription = undefined;
          this.externalAppService.removeApplication(frameName);
          this.formFacade.erase(formId);
          delete this.cobrowsings[bookingId];
        }
      });

      // we listen to the "screen update" event to get thee "last update" date in order to detect if the customer screen is still open
      this.cobrowsings[bookingId].context.on('screen.updated', screenInfo => {
        const currentCobrowsing = this.cobrowsings[bookingId];
        // if the cobrowsing is currently in a "timeout" state and the customer screen has been updated "recently", we remove the error
        if (
          currentCobrowsing.errorMessageKey === CobrowsingMessageKey.TIMEOUT &&
          currentCobrowsing.lastUpdate &&
          Date.now() - currentCobrowsing.lastUpdate < this.COBROWSING_SESSION_TIMEOUT
        ) {
          // if we were previoulsy in a "timeout" state, but the lastUpdate starts updating again (i.e. the customer reopened their tab)
          // then we remove the error state
          currentCobrowsing.errorMessageKey = undefined;
          this.cobrowsingFacade.updateCobrowsingState({
            bookingId,
            sessionState: currentCobrowsing.sessionState,
            errorMessageKey: undefined,
          });
        }
        currentCobrowsing.lastUpdate = screenInfo.updated?.getTime();
      });

      // we set a timeout to verify if there's no hanging session (e.g. the customer's tab/window has been closed)
      // this separate timer is needed because no event will be sent by cobrowse.io when the customer tab is closed
      if (!this.cobrowsings[bookingId].timeoutTimerSubscription) {
        this.cobrowsings[bookingId].timeoutTimerSubscription = timer(
          this.COBROWSING_SESSION_TIMEOUT,
          2000,
          this.zoneService.leaveScheduler()
        ).subscribe(() => {
          const currentCobrowsing = this.cobrowsings[bookingId];
          if (
            currentCobrowsing.lastUpdate &&
            Date.now() - currentCobrowsing.lastUpdate > this.COBROWSING_SESSION_TIMEOUT &&
            currentCobrowsing.errorMessageKey !== CobrowsingMessageKey.TIMEOUT
          ) {
            currentCobrowsing.errorMessageKey = CobrowsingMessageKey.TIMEOUT;
            this.cobrowsingFacade.updateCobrowsingState({
              bookingId,
              sessionState: currentCobrowsing.sessionState,
              errorMessageKey: CobrowsingMessageKey.TIMEOUT,
            });
          }
        });
      }
    }
  }

  /**
   * Gets the session corresponding to a given 6-digits code from the cobrowse.io server
   * @param bookingId the booking id of the interaction with cobrowsing
   * @param code the 6-digits code
   * @async
   */
  async getSession(bookingId: string, code: string): Promise<any> {
    const currentCobrowse = this.cobrowsings[bookingId]?.cobrowse;
    if (currentCobrowse && code) {
      this.logger.debug(`Trying to open a session with the customer-given code ${code} for booking ${bookingId}`);
      const session = await currentCobrowse.sessions.get(code);
      this.logger.debug(
        `Opened a session with the customer-given code ${code} for booking ${bookingId}, session id : ${session.id}. Pending for customer validation`
      );
      return session;
    }
  }

  /**
   * Positions and displays the external app relative to a given container
   * @param bookingId the booking id of the interaction with cobrowsing
   * @param container the Element the ext app should be positioned relative to
   */
  displayExternalApp(bookingId: string, container: ElementRef) {
    const currentCobrowsing = this.cobrowsings[bookingId];
    if (container?.nativeElement && currentCobrowsing?.sessionId) {
      const frameName = COBROWSING_FRAME_NAME_PREFIX + currentCobrowsing.sessionId;
      this.externalAppService.positionApplication(frameName, container.nativeElement);
      this.externalAppService.showApplication(frameName);
    }
  }

  setCobrowsingEndStatus(bookingId: string, endStatus: CobrowsingMessageKey | undefined, isSent: boolean) {
    this.isSent = isSent;
    this.cobrowsings[bookingId].errorMessageKey = endStatus;
  }

  /**
   * hides the external app
   * @param bookingId the booking id of the interaction with cobrowsing
   */
  hideExternalApp(bookingId: string) {
    const currentCobrowsing = this.cobrowsings[bookingId];
    if (currentCobrowsing?.sessionId) {
      const frameName = COBROWSING_FRAME_NAME_PREFIX + currentCobrowsing.sessionId;
      this.externalAppService.hideApplication(frameName);
    }
  }

  /**
   * End a cobrowsing session
   * @param bookingId the booking id of the interaction with cobrowsing
   */
  endSession(bookingId: string) {
    const currentCobrowsing = this.cobrowsings[bookingId];
    currentCobrowsing.toBeCleaned = true;
    currentCobrowsing.context?.endSession();
  }

  /**
   * End the cobrowsing when the interaction is ended.
   * Must be in the service since the cobrowsing component is destroyed as soon as the call ends
   * @param bookingId the booking id of the interaction with cobrowsing
   */
  cleanAfterInteractionEnd(bookingId: string) {
    const currentCobrowsing = this.cobrowsings[bookingId];
    currentCobrowsing?.context?.endSession(); // triggers the session.updated event asynchronously
    const formId = Formly.toId([COBROWSING_FORM_ID_PREFIX, bookingId]);
    this.formFacade.remove(formId);
  }

  /**
   * Gets the number of active sessions on the cobrowsing server.
   * @param bookingId
   */
  async getActiveSessionsNumber(bookingId: string): Promise<{ sessionNumber: number; errorMessageKey?: CobrowsingMessageKey }> {
    const currentCobrowsingInfos = this.cobrowsings[bookingId];
    try {
      const activeSessions = await currentCobrowsingInfos.cobrowse?.sessions.list({
        state: CobrowsingSessionState.active,
        agent: 'all',
        limit: `${this.maxActiveSessions}`,
      });
      return { sessionNumber: activeSessions?.length || 0 };
    } catch (e) {
      return { sessionNumber: 0, errorMessageKey: this.handleServerError(currentCobrowsingInfos, e) };
    }
  }

  /**
   * triggers the "start error" action with the correct label depending on the error received from the cobrowse.io server
   * Not very happy with that, but we have to use the error message itself to discriminate between errors
   * @param info information about the failed cobrowsing
   * @param e the error received from the cobrowse.io server
   * @private
   */
  private handleServerError(info: CobrowsingInfo, e: any): CobrowsingMessageKey {
    let errorMessageKey = CobrowsingMessageKey.UNKNOWN_ERROR;
    if (e.status === 404 && e.message === CobrowseIoServerMessages.SESSION_NOT_FOUND.valueOf()) {
      errorMessageKey = CobrowsingMessageKey.UNKNOWN_CODE;
      this.logger.error(
        `Wrong code entered (${info.sessionCode}), or corresponding session could not be found on cobrowse.io server for booking ${info.bookingId}`
      );
    } else if (e.status === 400 && e.message.includes(CobrowseIoServerMessages.ACCOUNT_NOT_FOUND.valueOf())) {
      errorMessageKey = CobrowsingMessageKey.INVALID_ACCOUNT_KEY;
      this.logger.error(`Could not reach the cobrowse API : [${e}]`);
    } else if (e.message === CobrowseIoServerMessages.NETWORK_REQUEST_FAILED.valueOf()) {
      this.logger.error(`The cobrowse server is unreachable. The server may be not running or the URL is incorrect : [${e}]`);
    } else {
      this.logger.error(`Unknown error when trying to start cobrowsing for booking ${info.bookingId}`);
    }
    return errorMessageKey;
  }

  /**
   *  Set the tool to use in the Agent session draw/laser.
   */
  setTool(bookingId: string, toolName: ToolName): boolean {
    if (!this.cobrowsings[bookingId].context) return false;
    this.cobrowsings[bookingId].context?.setTool(toolName);
    return true;
  }

  /**
   * Clear all agent session annotations from the user's view.
   */
  clearAnnotations(bookingId: string) {
    if (this.cobrowsings[bookingId].context) {
      this.cobrowsings[bookingId].context?.clearAnnotations();
    }
  }

  sendStartInfoForReporting(booking: Booking) {
    const body: CobrowsingReportingInfo = {
      bookingId: booking.responseId,
      connectionId: booking.connectionId,
      customerUuid: booking.customerId,
      direction: booking.channel.direction,
      flowId: booking.entryPoint.id,
      mediaType: booking.channel.type,
      startDate: Date.now(),
      userUuid: booking.agentId,
      skillsId: booking.skillsId,
      domainId: booking.domainId,
    };
    this.http.post<boolean>(`V2/${CngFacade.PROTECTED__COBROWSING}/startCobrowsingSession`, body).subscribe();
  }

  sendEndInfoForReporting(booking: Booking) {
    const body: CobrowsingReportingInfo = {
      connectionId: booking.connectionId,
      endDate: Date.now(),
      endReason: this.cobrowsings[booking.responseId].errorMessageKey,
      userUuid: booking.agentId,
    };
    this.http.post<boolean>(`V2/${CngFacade.PROTECTED__COBROWSING}/endCobrowsingSession`, body).subscribe();
  }

  initializeCobrowsingForBooking(bookingId: string) {
    return combineLatest({
      booking: this.bookingFacade.getById(bookingId).pipe(isNotNullOrUndefined()),
      rightIn: this.profileService.hasPermission$('CSR_PHONE_INCOMING::cobrowse:activate'),
      rightUnit: this.profileService.hasPermission$('CSR_UNITARY_PHONE_OUTGOING::cobrowse:activate'),
      rightOut: this.profileService.hasPermission$('CSR_PHONE_OUTGOING::cobrowse:activate'),
      domainName: this.profileFacade.domainName$,
    }).pipe(
      take(1),
      map(({ booking, rightIn, rightUnit, rightOut, domainName }) => {
        switch (true) {
          case !this.domainList.some(domain => domain === domainName.split('-').pop()):
            return false;
          case ChannelUtil.equals(booking.channel, ChanEnum.In.Phone):
            return rightIn;
          case ChannelUtil.equals(booking.channel, ChanEnum.Unit.Phone):
            return rightUnit;
          case ChannelUtil.equals(booking.channel, ChanEnum.Out.Phone):
            return rightOut;
          default:
            return false;
        }
      }),
      switchMap(can => {
        if (can) {
          return this.getCobrowsingJwt().pipe(
            map(jwt => ({ bookingId, jwt })),
            tap(info => {
              const cobrowse = new CobrowseAPI(info.jwt, { api: this.COBROWSE_SERVER_URL });
              this.cobrowsings[bookingId] = { ...info, cobrowse };
            })
          );
        } else {
          this.cobrowsingFacade.removeCobrowsing(bookingId);
          return EMPTY;
        }
      })
    );
  }
}
